import * as Sentry from '@sentry/react';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import { getCategoryName } from '../../fare-estimate-v1/domain/helpers/CategoryAnalytics.mjs';
import { publishPageLandEvent } from '../../../shared/core/helpers/CommonAnalyticsHelper.mjs';
import { make as DownloadAppSectionCmp } from '../../../shared/download-app/view/components/DownloadAppSectionCmp.mjs';
import { useCitySelectedContext } from '../../../contexts/city-context/CityContext';
import FaqsCmp from '../../../ui/components/FaqsCmp';
import LoadingSpinner from '../../../ui/components/LoadingSpinner';
import {
  HomeCity,
  HomeContentEntity,
} from '../domain/entities/HomeContentEntity';
import { GA_PAGE_LANDING } from '../domain/store/HomeStoreEn';

import { getCurrentCityData } from './view-model/HomeVMMapper';
import styles from './styles/HomeContainer.module.scss';

import EnterpriseBannerCmp from './components/EnterpriseBannerCmp';
import EnterpriseGrowthCardCmp from './components/EnterpriseGrowthCardCmp';
import HomeBannerWrapperCmp from './components/HomeBannerWrapperCmp';
import MiddleSectionCmp from './components/MiddleSectionCmp';
import ServicesCategoryCmp from './components/ServicesCategoryCmp';
import ServicesSectionCmp from './components/ServicesSectionCmp';

interface HomeContainerProps {
  page_data: HomeContentEntity;
  fare_estimate_form_content: unknown;
}

function HomeContainer({
  page_data,
  fare_estimate_form_content,
}: HomeContainerProps): React.ReactElement {
  const router = useRouter();
  const citySelectedContext = useCitySelectedContext();
  const [cityServices, setCityServices] = useState<HomeCity | undefined>(
    undefined,
  );
  const { cities, download_app_mobile, faqs, icons, service_title } = page_data;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const category = getCategoryName(router) as string;
      publishPageLandEvent(category, GA_PAGE_LANDING);
    }, 0);
    return (): void => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (citySelectedContext.city_selected !== undefined) {
      try {
        setCityServices(
          getCurrentCityData(cities, citySelectedContext.city_selected),
        );
      } catch {
        Sentry.captureMessage(
          `Home Local Storage: Error finding city: ${citySelectedContext.city_selected}`,
        );
      }
    } else if (cities.length > 0) {
      const firstCity = cities[0];
      citySelectedContext.set_city_selected(
        firstCity.city_name,
        firstCity.city_label,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySelectedContext.city_selected, router.locale]);

  return (
    <>
      <HomeBannerWrapperCmp data={page_data} />
      <ServicesCategoryCmp
        city_selector_data={cities}
        city_selected={cityServices}
        fare_estimate_form_content={fare_estimate_form_content}
        content={page_data}
      />
      <div className={styles['mobile-only']}>
        <EnterpriseBannerCmp page_data={page_data} />
      </div>
      <div className={styles['desktop-only']}>
        <EnterpriseGrowthCardCmp
          section_data={page_data.enterprise_home_section}
        />
      </div>
      {cityServices ? (
        <ServicesSectionCmp
          services_cards_data={cityServices.city_services_promo}
          wave_icon={icons.wave_icon}
          title={service_title}
        />
      ) : (
        <div className={styles['section-loading']}>
          <LoadingSpinner />
        </div>
      )}
      <DownloadAppSectionCmp data={download_app_mobile} />
      <MiddleSectionCmp data={page_data} />
      <FaqsCmp faqs_section={faqs} />
    </>
  );
}

export default HomeContainer;
